import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // close the drawer on viewport changes
  useEffect(() => {
    const closeOnRoute = () => setIsMobileOpen(false);
    window.addEventListener("resize", closeOnRoute);
    return () => window.removeEventListener("resize", closeOnRoute);
  }, []);

  // fetch unread count for notifications badge (derive from /contact like Notifications page)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/contact");
        const msgs = Array.isArray(res.data)
          ? res.data
          : res.data?.data ?? res.data ?? [];
        const unread = (msgs || []).filter((m: any) => !m?.isRead).length;
        if (alive) setUnreadCount(unread);
      } catch {
        // keep silent in sidebar
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const links = [
    { name: "Dashboard", path: "/dashboard" },
    { name: "Cars", path: "/cars" },
    { name: "Bookings", path: "/bookings" },
    { name: "Users", path: "/users" },
    { name: "Notifications", path: "/notifications" },
    { name: "Region Rates", path: "/admin/edit-region-rates" },
  ];

  const isActivePath = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <>
      {/* Mobile toggle */}
      <div className="fixed z-50 md:hidden top-4 left-4">
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="px-3 py-2 text-white bg-blue-900 rounded"
          aria-label="Toggle sidebar"
        >
          ☰
        </button>
      </div>

      <aside
        className={`w-64 h-full rounded-md mt-2 ml-2 bg-blue-900 text-white flex flex-col px-4 py-6 transition-transform duration-300 z-40
        ${isMobileOpen ? "fixed top-0 left-4" : "hidden"} 
        md:static md:flex`}
      >
        {/* User Info */}
        <div className="flex flex-col items-center justify-center mb-6">
          <img
            src="/images/man-user-svgrepo-com.svg"
            alt="User Icon"
            className="object-contain w-16 h-16 p-2 bg-white rounded-full"
          />
          <p className="mt-2 text-lg font-semibold text-yellow-300 capitalize">
            {user?.role || "Admin"}
          </p>
          <p className="text-sm text-center text-white">
            {user?.email || "user@example.com"}
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col items-start gap-4 mx-auto mt-2 w-max">
          {links.map((link) => {
            const active = isActivePath(link.path);
            const baseClass =
              "text-2xl font-medium transition rounded-md px-2 py-1 w-full text-left";
            const stateClass = active
              ? "text-yellow-300 bg-white bg-opacity-10"
              : "text-white hover:text-yellow-400";
            const className = `${baseClass} ${stateClass}`;

            // Special rendering for Notifications to include a badge
            if (link.name === "Notifications") {
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMobileOpen(false)}
                  className={className}
                >
                  <span className="inline-flex items-center gap-2">
                    <span>{link.name}</span>
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center justify-center rounded-full bg-red-600 text-white text-xs px-2 py-0.5">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </span>
                </Link>
              );
            }

            return (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsMobileOpen(false)}
                className={className}
              >
                {link.name}
              </Link>
            );
          })}

          {/* Editor button */}
          <button
            onClick={() => {
              navigate("/edit");
              setIsMobileOpen(false);
            }}
            className={`text-2xl font-medium transition rounded-md px-2 py-1 w-full text-left ${
              isActivePath("/edit")
                ? "text-yellow-300 bg-white bg-opacity-10"
                : "text-white hover:text-yellow-400"
            }`}
          >
            Editor
          </button>

          {/* System Audit link (you already route to /audit) */}
          <button
            onClick={() => {
              navigate("/audit");
              setIsMobileOpen(false);
            }}
            className={`text-2xl font-medium transition rounded-md px-2 py-1 w-full text-left ${
              isActivePath("/audit")
                ? "text-yellow-300 bg-white bg-opacity-10"
                : "text-white hover:text-yellow-400"
            }`}
          >
            System Audit
          </button>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
